import React from "react";
import { Query } from "react-apollo";
import { gql } from "apollo-boost";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import orderBy from "lodash/orderBy";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";

import ChooseColor from "./ChooseColor";
import Loader from "../Loader";

import { hasSingleSideColor } from "../../helpers";

const useStyles = makeStyles(theme => ({
  box: {
    "& > *:not(:last-child)": {
      marginRight: props => (props.isMobile ? 0 : theme.spacing(3))
    },
    "& > *:not(:first-child)": {
      marginTop: props => (props.isMobile ? theme.spacing(3) : 0)
    },
    "& > *": {
      flexGrow: 1,
      width: props => (props.isMobile ? "100%" : 300),
      maxWidth: 300,
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%"
      }
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      justifyContent: "center"
    }
  }
}));

export default function ChooseColors({
  defaultColor,
  focusColorExterior,
  focusColorInterior,
  materialId,
  onSelectColor,
  onSelectColorExterior,
  onSelectColorInterior,
  productId,
  selectedColors,
  setSelectedColors,
  systemId
}) {
  const isMobile = useMediaQuery("(max-width:600px)");

  const { t } = useTranslation();
  const classes = useStyles({ isMobile });

  return (
    <Query query={COLORS_QUERY} variables={{ materialId, productId, systemId }}>
      {({ data, loading, error, refetch }) => {
        if (loading) {
          return (
            <div className="flex w-100">
              <Loader />
            </div>
          );
        }

        if (error) {
          return (
            <div className="flex w-100">
              <div>{t("root.error")}</div>
            </div>
          );
        }

        const options = orderBy(
          data.colors.map(({ id, image, name }) => ({
            id,
            image: image && `/img/colors/${image}`,
            name,
            translation: t(`names.colors.${name}`)
          })),
          "translation",
          "asc"
        );

        return (
          <>
            <h2>{t("categories.color")}</h2>
            {hasSingleSideColor(productId) ? (
              <Box className={classes.box} display="flex" flexWrap="wrap">
                <ChooseColor
                  focus={focusColorInterior}
                  defaultColor={defaultColor}
                  label={t("categories.color")}
                  options={options}
                  selectedColor={selectedColors.color}
                  setSelectedColor={id =>
                    setSelectedColors(prevSelection => ({
                      ...prevSelection,
                      color: id
                    }))
                  }
                  onSelect={onSelectColor}
                />
              </Box>
            ) : (
              <Box className={classes.box} display="flex" flexWrap="wrap">
                <ChooseColor
                  focus={focusColorInterior}
                  defaultColor={defaultColor}
                  label={t("creator.interior")}
                  options={options}
                  selectedColor={selectedColors.interior}
                  setSelectedColor={id =>
                    setSelectedColors(prevSelection => ({
                      ...prevSelection,
                      interior: id
                    }))
                  }
                  onSelect={onSelectColorInterior}
                />
                <ChooseColor
                  focus={focusColorExterior}
                  defaultColor={defaultColor}
                  label={t("creator.exterior")}
                  options={options}
                  selectedColor={selectedColors.exterior}
                  setSelectedColor={id =>
                    setSelectedColors(prevSelection => ({
                      ...prevSelection,
                      exterior: id
                    }))
                  }
                  onSelect={onSelectColorExterior}
                />
              </Box>
            )}
          </>
        );
      }}
    </Query>
  );
}

export const COLORS_QUERY = gql`
  query ColorsQuery($materialId: ID, $productId: ID, $systemId: ID) {
    colors(
      materialId: $materialId
      productId: $productId
      systemId: $systemId
    ) {
      id
      name
      image
    }
  }
`;
